import { useContext, useEffect, useState, useRef } from 'react';
import { Row, Col, Collapse } from 'antd';
import { any, array, object, string } from 'prop-types';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';

import Layout from 'components/layout';
import HeadSEO from 'components/base/headSEO';
import { defaultLocale } from 'utils/translations';
import { getLandingCMSProps, initCMS } from 'utils/cms';
import { getFAQs } from 'actions/general';
import { i18nContext } from 'context/i18n';
import { TrackingSVG } from 'components/iconsSVG/tracking';
import { ReturnSVG } from 'components/iconsSVG/return';
import { ChevronSVG } from 'components/iconsSVG/chevron';
import { getIsMobile } from 'utils/helpers';

import styles from 'styles/CustomerService.module.css';
import { getDomainValue } from 'utils/domain';

const ProductsRow = dynamic(() => import('components/ProductsRow'));
const Product = dynamic(() => import('components/product'));

const MyOrderModal = dynamic(() => import('components/myOrderModal').then((mod) => mod.MyOrderModal));
const GlobalEOrdersWidget = dynamic(() => import('components/myOrderModal').then((mod) => mod.GlobalEOrdersWidget));

const LandingComponent = getDomainValue({
  camper: dynamic(() => import('@camper/react-web-components').then((res) => res.PAGES.LandingComponent)),
  camperlab: dynamic(() => import('@camper/react-web-components').then((res) => res.PAGES.LandingComponent)),
  nnormal: dynamic(() => import('@nnormal/react-web-components').then((res) => res.PAGES.DynamicLanding)),
});

const { Panel } = Collapse;

function CustomerService({ defaultProps, locale = defaultLocale, categories = [], initialCategory, contentProps = null }) {
  const { t, profileData } = useContext(i18nContext);
  const { global_e } = profileData;
  const faqStartRef = useRef(null);
  const { query } = useRouter();

  const returnCategory = categories.find((c) => c.id === 5);
  const [activeCategory, setActiveCategory] = useState(query?.type === 'returns' && !query.category && returnCategory ? returnCategory : initialCategory);

  const [breadcrumbFragments, setBreadcrumbFragments] = useState([]);
  const [showingMyOrderModal, setShowingMyOrderModal] = useState(false);

  const isMobile = getIsMobile('767.5px');

  // Breadcrumbs
  useEffect(() => {
    const breadcrumbs = [];
    breadcrumbs.push({
      title: t('generico', 'breadcrumb.home', '**Home'),
      href: `/${locale}/`,
    });
    breadcrumbs.push({
      title: t('faq', 'customer.service', 'Customer Service'),
      href: `/${locale}/customer_service`,
    });
    setBreadcrumbFragments(breadcrumbs);
  }, []);

  const onChangeAccordion = (key) => {
    if (key) {
      setActiveCategory(categories.filter((category) => category.categoryName === key)[0]);
      if (window && window.history.replaceState) {
        window.history.replaceState({}, '', `?category=${key}`);
      }
    }
    const isDesktop = !Number.isNaN(document?.documentElement?.clientWidth) && document?.documentElement?.clientWidth > 768;
    if (isDesktop && faqStartRef.current) {
      faqStartRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const featuredFaqs = categories.map((category) => category.faqs.filter((faq) => faq.featured)).flat();
  featuredFaqs.sort((a, b) => {
    if (a.featuredSort > b.featuredSort) {
      return 1;
    }
    return b.featuredSort > a.featuredSort ? -1 : 0;
  });

  let orderModal = <MyOrderModal locale={locale} visible={showingMyOrderModal} onClose={() => setShowingMyOrderModal(false)} />;

  if (global_e) {
    orderModal = (
      <GlobalEOrdersWidget
        onOpen={() => setShowingMyOrderModal(true)}
        visible={showingMyOrderModal}
        onlyModal={true}
        onClose={() => setShowingMyOrderModal(false)}
      />
    );
  }

  return (
    <Layout
      {...defaultProps}
      breadcrumbFragments={breadcrumbFragments}
      extratags={[<link key="canonical" rel="canonical" href={`${process.env.INTEGRA_DOMAIN}/${locale}/customer_service`} />]}
      pageName="customer_service"
    >
      <div>
        <HeadSEO title="Atención al cliente" description="Customer Service" />
        <main>
          {contentProps ?
            <LandingComponent
              slots={{
                products: ProductsRow,
                product: Product,
              }}
              {...contentProps}
            />
          : null}
          <Row id="faqscontent" ref={faqStartRef}>
            {orderModal}
            <Col span={24} xxl={{ span: 16, offset: 4 }}>
              <div className={styles.yourOrder}>
                <h1 className={styles.ordersTitle}>{t('faqs', 'titulo.your.order', 'Manage Your Order')}</h1>
                <div className={styles.manageOrder}>
                  <div className={styles.manageItem} onClick={() => setShowingMyOrderModal(true)}>
                    <TrackingSVG />
                    <span>{t('faqs', 'manage.order.tracking', 'Tracking Order')}</span>
                  </div>
                  {!global_e ?
                    <div className={styles.manageItem} onClick={() => setShowingMyOrderModal(true)}>
                      <ReturnSVG />
                      <span>{t('faqs', 'manage.order.size.exchange', 'Size Exchange')}</span>
                    </div>
                  : null}
                  <div className={styles.manageItem} onClick={() => setShowingMyOrderModal(true)}>
                    <ReturnSVG />
                    <span>{t('faqs', 'manage.order.return.order', 'Return Order')}</span>
                  </div>
                </div>
              </div>
              {featuredFaqs && featuredFaqs.length && (
                <div className={styles.featuredFaqsMain}>
                  <h2 className={styles.title}>{t('faqs', 'titulo.preguntas.frecuentes', 'Frequent questions')}</h2>
                  <div>
                    {featuredFaqs.map((faq) => (
                      <p className={styles.question} key={faq.id}>
                        <a href={`/${locale}/at_cliente_faq?faq=${faq.id}`}>{faq.question}</a>
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </Col>
          </Row>
          <Row>
            <Col span={24} xxl={{ span: 16, offset: 4 }} className={styles.featuredFaqs}>
              <h2 className={`${styles.title} ${styles.topics}`}>{t('faqs', 'titulo.topicos', 'Help topics')}</h2>
              <Row>
                <Col xs={24} md={12}>
                  <Collapse
                    bordered={false}
                    expandIcon={({ isActive }) => (
                      <span className={isMobile && isActive ? styles.chevronActive : styles.chevron}>
                        <ChevronSVG />
                      </span>
                    )}
                    expandIconPosition="right"
                    accordion
                    onChange={onChangeAccordion}
                    defaultActiveKey={activeCategory ? activeCategory.categoryName : ''}
                  >
                    {categories.map((category) => (
                      <Panel
                        header={
                          <span className={`${styles.accordionHeader} ${!isMobile && activeCategory.id === category.id ? styles.accordionActive : ''}`}>
                            {category.text}
                          </span>
                        }
                        key={category.categoryName}
                      >
                        {isMobile && (
                          <div className={styles.content}>
                            {category.faqs.map((faq) => (
                              <p className={styles.question} key={`${faq.categoryName}${faq.id}`}>
                                <a href={`/${locale}/at_cliente_faq?faq=${faq.id}`}>{faq.question}</a>
                              </p>
                            ))}
                          </div>
                        )}
                      </Panel>
                    ))}
                  </Collapse>
                </Col>
                {!isMobile && (
                  <Col span={12} className={styles.featuredFaqs}>
                    {activeCategory.faqs.map((faq) => (
                      <p className={styles.question} key={`${faq.categoryName}-${faq.question}-${faq.id}`}>
                        <a href={`/${locale}/at_cliente_faq?faq=${faq.id}`}>{faq.question}</a>
                      </p>
                    ))}
                  </Col>
                )}
              </Row>
            </Col>
          </Row>
          <Row>
            <Col xs={{ span: 22, offset: 1 }} md={{ span: 8, offset: 8 }} className={styles.contactBox}>
              <p>{t('faqs', 'texto.ayuda', "Can't find what you're looking for?")}</p>
              <a href={`/${locale}/contacto`}>{t('faqs', 'boton.contacto', 'Contact us')}</a>
            </Col>
          </Row>
        </main>
      </div>
    </Layout>
  );
}

CustomerService.propTypes = {
  locale: string,
  footerProps: any,
  tnmProps: any,
  ribbon: any,
  categories: array,
  initialCategory: object,
  contentProps: any,
};

CustomerService.i18nPage = 'CustomerService';

export async function getServerSideProps(context) {
  const { locale, query } = context;

  // category = el string que identifica la categoría
  // tit = es el número de categoría. Si llega este parámetro, mostramos todas las FAQs de la categoría
  const { category, tit, type } = query;

  // Se pide al CMS parte de una landing cuando queremos pintar algo antes de los faqs. Ejemplo: https://www.camper.com/en_ES/customer_service?type=returns
  const landingPromise = typeof type === 'string' ? getLandingCMSProps({ locale, slug: `/${type}` }) : Promise.resolve(null);

  const [{ defaultProps }, { categories }, landingProps] = await Promise.all([
    initCMS({ context, locale }),
    getFAQs({ context, lang: locale }),
    landingPromise,
  ]);

  let initialCategory = null;
  if (categories && categories.length) {
    if (category) {
      [initialCategory] = categories.filter((cat) => cat.categoryName === category);
    } else if (tit) {
      [initialCategory] = categories.filter((cat) => cat.id === Number(tit));
    }

    if (!initialCategory) {
      [initialCategory] = categories;
    }
  }

  return {
    props: {
      defaultProps,
      locale,
      categories: categories ?? [],
      initialCategory,
      contentProps: landingProps?.contentProps ?? null,
    },
  };
}

export default CustomerService;
